import React from 'react';

import { useLazyIbanCodeLookupQuery } from '@/api/v1';
import { CountryType } from '@/types/country';
import { isProbablyValidIBAN } from '@/utility/iban';
import { normalizeSwiftCode, remapSwiftCode, swiftBic8 } from '@/utility/swift';

type ValidatorFn = (
  ibanCode: string,
  swiftCode: string,
  countryCode: CountryType,
) => Promise<string | null>;

export const IBAN_VALIDATION_INVALID_IBAN = 'IBAN_VALIDATION_INVALID_IBAN';
export const IBAN_VALIDATION_NOT_FOUND = 'IBAN_VALIDATION_NOT_FOUND';
export const IBAN_VALIDATION_INVALID_COUNTRY = 'IBAN_VALIDATION_INVALID_COUNTRY';
export const INVALID_SWIFT_CODE_FOR_IBAN = 'INVALID_SWIFT_CODE_FOR_IBAN';

export class IBANSwiftValidationException extends Error {
  public readonly field: 'iban' | 'swiftCode';
  public readonly parameters: readonly string[];

  constructor(field: 'iban' | 'swiftCode', message: string, ...parameters: readonly string[]) {
    super(message);

    this.name = field;
    this.field = field;
    this.parameters = parameters;
  }
}

export const useIBANAndSwiftValidation = (): ValidatorFn => {
  const [ibanLookup] = useLazyIbanCodeLookupQuery();

  return React.useCallback(
    async (
      ibanCode: string,
      swiftCode: string,
      countryCode: CountryType,
    ): Promise<string | null> => {
      const normalizedSwiftCode = normalizeSwiftCode(swiftCode);

      if (!isProbablyValidIBAN(ibanCode)) {
        return null;
      }

      const { data: { data, success } = { data: null, success: false }, isError } =
        await ibanLookup(ibanCode);
      if (isError || !success) {
        throw new IBANSwiftValidationException('iban', IBAN_VALIDATION_INVALID_IBAN);
      }

      if (!data) {
        throw new IBANSwiftValidationException('iban', IBAN_VALIDATION_NOT_FOUND);
      }

      const { swift, country } = data;
      const remappedApiSwift = remapSwiftCode(swift.id);
      const apiSwiftBic8 = swiftBic8(remappedApiSwift);
      if (
        normalizedSwiftCode &&
        apiSwiftBic8 &&
        apiSwiftBic8 !== swiftBic8(remapSwiftCode(swiftCode))
      ) {
        throw new IBANSwiftValidationException('swiftCode', INVALID_SWIFT_CODE_FOR_IBAN);
      }

      if (country.id !== countryCode) {
        throw new IBANSwiftValidationException(
          'iban',
          IBAN_VALIDATION_INVALID_COUNTRY,
          country.id,
          countryCode,
        );
      }

      return remappedApiSwift || null;
    },
    [ibanLookup],
  );
};
